import React, { useEffect, useMemo, useState, useCallback } from 'react';
import { useSearchParams } from 'react-router-dom';
import * as api from '../services/api';
import {
  Activity, ServerCog, Zap, AlertTriangle, CheckCircle, XCircle, RefreshCw,
  Loader, ChevronDown, ChevronRight, Cpu, Database, Settings, Shield, Cog,
  Trash2, AlertCircle, Wrench, Network, Gauge, Clock, Info, ExternalLink,
  Sparkles, Layers, ShieldCheck,
} from 'lucide-react';
import PageHeader from '../components/common/PageHeader';
import InferencePage from './InferencePage';
import LlmOperationsTab from '../components/v2/admin/LlmOperationsTab';
import ProductCatalogPanel from '../components/v2/admin/ProductCatalogPanel';
import PlatformConnectorsTab from '../components/v2/admin/PlatformConnectorsTab';
import CommandLimitsTab from '../components/v2/admin/CommandLimitsTab';
import { Modal } from '../components/common/Modal';
import { useAuth } from '../contexts/AuthContext';
/* G97 — full v2 redesign of AdminCenterPage. Replace custom 100vh + tab
 * bar chrome with V2PageShell + V2TabNav. Sub-tab components
 * (OverviewTab, WorkflowRuntimeTab, WorkerTuningTab, ManagedSkillsTab,
 * RecoveryTab, embedded InferencePage) are unchanged — they own their
 * own data + business logic. */
import {
  V2PageShell,
  TabNav as V2TabNav,
  Pill as V2Pill,
} from '../components/v2';

/**
 * AdminCenterPage — single pane of glass for platform runtime operations.
 *
 * <h3>Why this page exists</h3>
 * <p>Before this page, an admin who needed to know "is the workflow runtime
 * healthy?" had to: tail backend logs, shell into the Temporal OSS pod, eyeball
 * grafana, ask another engineer. That's not the experience we want for a
 * production launch — Temporal OSS is the platform's backbone and admins need
 * to detect-and-act inside the product, not in OSS Temporal OSS's UI.</p>
 *
 * <h3>Tabs</h3>
 * <ol>
 *   <li><b>Overview</b> — runtime health snapshot. Posture pill (healthy /
 *       degraded / critical) with reasons; cluster connectivity card;
 *       in-flight + wedged workflow counters; last-24h success rate +
 *       percentiles.</li>
 *   <li><b>Workflow Runtime</b> — in-flight workflow table with filters,
 *       per-row terminate, bulk terminate-wedged. Direct equivalent of
 *       the runbook's recovery procedure.</li>
 *   <li><b>Worker Tuning</b> — read-only view of every {@code temporal.*}
 *       property in effect, with the env var beside each value. Edits
 *       go via deploy config — the page documents the override path
 *       instead of mutating live worker config (footgun avoidance).</li>
 *   <li><b>Agent Inference</b> — existing Inference Controls embedded so
 *       admins have one stop for "all platform runtime config".</li>
 *   <li><b>Recovery & Cleanup</b> — destructive bulk operations gated to
 *       Super / Platform Admin with confirmation modals.</li>
 * </ol>
 *
 * <h3>Auth</h3>
 * <p>Read tabs (Overview, Workflow Runtime list, Worker Tuning, Agent
 * Inference): Tenant Admin and above. Destructive operations
 * (single-terminate, bulk-terminate): Super / Platform Admin — gated by
 * the backend, the UI hides the buttons accordingly.</p>
 */

/* ══════════════════════════════════════════════════════════════════════════════
   DESIGN SYSTEM (mirrors PolicyConfig / WorkflowAudit / WorkflowIntelligence)
   ══════════════════════════════════════════════════════════════════════════════ */

const BRAND = {
  primary: '#0d9488', dark: '#0a766b', light: '#e3f4f1',
  glow: 'rgba(226, 74, 58, 0.06)', soft: '#f8d7d4',
  gradient: 'linear-gradient(135deg, #0d9488 0%, #0a766b 50%, #a03025 100%)',
  gradientSubtle: 'linear-gradient(135deg, rgba(226,74,58,0.04) 0%, rgba(197,58,43,0.02) 100%)',
};

const S = {
  0: '#ffffff', 25: '#fdfdfc', 50: '#fafaf9', 100: '#f5f5f4', 150: '#eeeded',
  200: '#e7e5e4', 300: '#d6d3d1', 400: '#a8a29e', 500: '#78716c',
  600: '#57534e', 700: '#44403c', 800: '#292524', 900: '#1c1917',
};

const ACCENT = {
  emerald: { bg: '#ecfdf5', text: '#065f46', border: '#a7f3d0', icon: '#10b981' },
  amber:   { bg: '#fffbeb', text: '#92400e', border: '#fde68a', icon: '#f59e0b' },
  sky:     { bg: '#f0f9ff', text: '#0c4a6e', border: '#bae6fd', icon: '#0ea5e9' },
  rose:    { bg: '#fff1f2', text: '#9f1239', border: '#fecdd3', icon: '#f43f5e' },
  violet:  { bg: '#f5f3ff', text: '#5b21b6', border: '#ddd6fe', icon: '#8b5cf6' },
  slate:   { bg: '#f8fafc', text: '#475569', border: '#e2e8f0', icon: '#64748b' },
};

const TABS = [
  { key: 'overview', label: 'Overview', icon: Gauge,
    desc: 'Runtime health at a glance — posture, cluster, in-flight workflows, last-24h percentiles.' },
  { key: 'workflow-runtime', label: 'Workflow Runtime', icon: Activity,
    desc: 'In-flight workflow operations — filter, inspect, terminate single or bulk.' },
  { key: 'worker-tuning', label: 'Worker Tuning', icon: ServerCog,
    desc: 'Read-only view of every temporal.* property in effect. Edit via deploy config.' },
  { key: 'agent-inference', label: 'Agent Inference', icon: Cpu,
    desc: 'Inference model controls — provider selection, rate limits, fallbacks.' },
  // Key kept as 'ai-models' so existing ?tab=ai-models links still resolve; the surface
  // is now the unified LLM Operations view (providers + routing + cost controls + usage).
  { key: 'ai-models', label: 'LLM Operations', icon: Layers,
    desc: 'Connect a provider once — providers, capability routing, cost controls, and this workspace’s usage and budget.' },
  { key: 'catalog', label: 'Catalog', icon: Database,
    desc: 'Edition plans, module SKUs, and cloud marketplace listings.' },
  // Platform Admin only — platform-owned relays (email, …) for invoices + invitations.
  { key: 'platform-connectors', label: 'Platform Connectors', icon: Shield, platformAdminOnly: true,
    desc: 'Platform-owned relays used for invoices and invitations. Kept separate from customer connectors. Platform Admin only.' },
  // The per-customer ceiling on a governed command's arguments. Super Admin (and Platform Admin,
  // who sits above it) only: the backend is authoritative and refuses a lesser role, and showing an
  // editable control that always fails is a worse surface than not showing it.
  { key: 'command-limits', label: 'Command Limits', icon: ShieldCheck, superAdminOnly: true,
    desc: 'The most a single governed action may carry in its arguments, for this customer.' },
  // Phase 7 — Auto-Skill Lifecycle.
  { key: 'managed-skills', label: 'Managed Skills', icon: Sparkles,
    desc: 'Wizard-owned policy skills. Auto-generated from framework bindings — re-bind from the wizard, not here.' },
  { key: 'recovery', label: 'Recovery & Cleanup', icon: Wrench,
    desc: 'Destructive bulk operations. Super / Platform Admin only.' },
];

const POSTURE_META = {
  healthy:  { label: 'Healthy',  accent: ACCENT.emerald, Icon: CheckCircle },
  degraded: { label: 'Degraded', accent: ACCENT.amber,   Icon: AlertTriangle },
  critical: { label: 'Critical', accent: ACCENT.rose,    Icon: XCircle },
};

const btnBase = {
  border: 'none', cursor: 'pointer', borderRadius: 8, fontFamily: 'inherit',
  display: 'inline-flex', alignItems: 'center', gap: 6, transition: 'all 0.15s ease',
};

const GlobalStyles = () => (
  <style>{`
    @keyframes acpSpin { from { transform: rotate(0deg); } to { transform: rotate(360deg); } }
    @keyframes acpPulse { 0%, 100% { opacity: 1; } 50% { opacity: 0.4; } }
    .acp-card { transition: all 0.2s cubic-bezier(0.4, 0, 0.2, 1) !important; }
    .acp-card:hover { border-color: ${BRAND.primary}30 !important; box-shadow: 0 4px 20px ${BRAND.glow}, 0 1px 3px rgba(0,0,0,0.04) !important; }
    .acp-row:hover { background: ${S[25]} !important; }
    .acp-tab:hover { color: ${S[700]} !important; background: ${S[50]} !important; }
    .acp-btn-ghost:hover { background: ${S[100]} !important; }
    .acp-btn-danger:hover { filter: brightness(1.05); }
  `}</style>
);

export default function AdminCenterPage() {
  const auth = useAuth();
  const isPlatformAdmin = auth?.session?.role === 'Platform Admin';
  // Platform-Admin-only tabs (e.g. Platform Connectors) are hidden for everyone else.
  // Super Admin is a customer-scoped administrator; Platform Admin sits above it and sees
  // everything a Super Admin does.
  const isSuperAdminOrAbove = isPlatformAdmin || auth?.session?.role === 'Super Admin';

  const visibleTabs = useMemo(
    () => TABS.filter((t) => (!t.platformAdminOnly || isPlatformAdmin)
                          && (!t.superAdminOnly || isSuperAdminOrAbove)),
    [isPlatformAdmin, isSuperAdminOrAbove],
  );

  const [searchParams, setSearchParams] = useSearchParams();
  const [activeTab, setActiveTab] = useState(searchParams.get('tab') || 'overview');

  useEffect(() => {
    const next = new URLSearchParams();
    if (activeTab && activeTab !== 'overview') next.set('tab', activeTab);
    setSearchParams(next, { replace: true });
  }, [activeTab, setSearchParams]);

  const activeTabMeta = visibleTabs.find((t) => t.key === activeTab) || visibleTabs[0];

  /* G97 — v2 TabNav config. Description bar below tabs is preserved
   * (active-tab-meta hint is a useful operator UX cue we keep). */
  const v2Tabs = visibleTabs.map(t => ({ id: t.key, label: t.label }));

  return (
    <V2PageShell
      title="Admin Center"
      subtitle="Single pane of glass for platform runtime — health, workers, recovery, and inference."
      tags={<V2Pill tone="cyan" size="sm">Administer</V2Pill>}
    >
      <GlobalStyles />

      <V2TabNav
        tabs={v2Tabs}
        activeId={activeTab}
        onChange={setActiveTab}
        ariaLabel="Admin Center sections"
      />

      {/* Active-tab description strip (preserved from v1 — useful operator hint) */}
      <div className="px-3 py-2 mt-3 mb-4 rounded-v2-sm border border-v2-border-subtle bg-v2-muted text-[12px] text-v2-ink-secondary flex items-center gap-2">
        {React.createElement(activeTabMeta.icon, { size: 12 })}
        <span>{activeTabMeta.desc}</span>
      </div>

      <div className="overflow-y-auto">
        {activeTab === 'overview' && <OverviewTab setActiveTab={setActiveTab} />}
        {activeTab === 'workflow-runtime' && <WorkflowRuntimeTab />}
        {activeTab === 'worker-tuning' && <WorkerTuningTab />}
        {activeTab === 'agent-inference' && <InferencePage embedded={true} />}
        {activeTab === 'ai-models' && <LlmOperationsTab />}
        {activeTab === 'catalog' && <ProductCatalogPanel />}
        {activeTab === 'platform-connectors' && isPlatformAdmin && <PlatformConnectorsTab />}
        {activeTab === 'command-limits' && isSuperAdminOrAbove && <CommandLimitsTab />}
        {activeTab === 'managed-skills' && <ManagedSkillsTab />}
        {activeTab === 'recovery' && <RecoveryTab />}
      </div>
    </V2PageShell>
  );
}

/* ══════════════════════════════════════════════════════════════════════════════
   OVERVIEW TAB — health dashboard
   ══════════════════════════════════════════════════════════════════════════════ */

function OverviewTab({ setActiveTab }) {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  const reload = useCallback(async () => {
    setLoading(true); setError(null);
    try {
      const res = await api.getTemporalHealth();
      if (res.ok) setData(res.data);
      // G585 — apiFetch returns {ok:false, error:'...'} on non-2xx; the
      // previous res.data?.message was always undefined (res.data is only
      // set on success), so every server error printed the generic
      // "Failed to load" and hid the real reason. Read res.error first.
      else setError(res.error || res.data?.message || 'Failed to load');
    } catch (e) { setError(e.message); }
    finally { setLoading(false); }
  }, []);

  useEffect(() => { reload(); }, [reload]);

  // Auto-refresh every 60s. Was 30s but the 24h workflow rollup is the
  // hottest aggregate query in the platform; halving the polling rate
  // halves backend load without meaningfully changing operator
  // experience. Pause polling when the browser tab is hidden so 100
  // background tabs don't multiply the load.
  useEffect(() => {
    let id = null;
    const start = () => {
      stop();
      id = setInterval(reload, 60000);
    };
    const stop = () => {
      if (id) { clearInterval(id); id = null; }
    };
    const onVisibility = () => {
      if (document.hidden) stop();
      else { reload(); start(); }
    };
    start();
    document.addEventListener('visibilitychange', onVisibility);
    return () => {
      stop();
      document.removeEventListener('visibilitychange', onVisibility);
    };
  }, [reload]);

  if (loading && !data) return <BodyLoading msg="Loading runtime health…" />;
  if (error) return <BodyError msg={error} onRetry={reload} />;
  if (!data) return null;

  const posture = data.posture || { level: 'healthy', reasons: [] };
  const pm = POSTURE_META[posture.level] || POSTURE_META.healthy;
  const cluster = data.cluster || {};
  const wf = data.workflows || {};

  return (
    <div style={{ padding: '20px 28px 32px' }}>
      {/* Posture banner */}
      <div className="acp-card" style={{
        background: '#fff', border: `1px solid ${pm.accent.border}`, borderRadius: 12,
        marginBottom: 16, overflow: 'hidden',
      }}>
        <div style={{ height: 4, background: pm.accent.icon }} />
        <div style={{ padding: '18px 24px', display: 'flex', alignItems: 'center', gap: 16 }}>
          <div style={{
            width: 48, height: 48, borderRadius: 12,
            background: pm.accent.bg, border: `1px solid ${pm.accent.border}`,
            display: 'flex', alignItems: 'center', justifyContent: 'center',
          }}>
            <pm.Icon size={24} color={pm.accent.icon} strokeWidth={2.2} />
          </div>
          <div style={{ flex: 1 }}>
            <div style={{ display: 'flex', alignItems: 'center', gap: 10 }}>
              <h2 style={{ margin: 0, fontSize: 18, fontWeight: 800, color: S[900],
                letterSpacing: '-0.01em' }}>
                Platform is {pm.label}
              </h2>
              <span style={{
                fontSize: 9, fontWeight: 700, color: pm.accent.text,
                background: pm.accent.bg, border: `1px solid ${pm.accent.border}`,
                padding: '3px 10px', borderRadius: 6,
                textTransform: 'uppercase', letterSpacing: '0.06em',
                fontFamily: "'IBM Plex Mono', 'SF Mono', monospace",
              }}>
                {posture.level}
              </span>
            </div>
            {posture.reasons && posture.reasons.length > 0 && (
              <div style={{ marginTop: 6, fontSize: 12, color: S[600] }}>
                {posture.reasons.join(' · ')}
              </div>
            )}
            {(!posture.reasons || posture.reasons.length === 0) && (
              <div style={{ marginTop: 6, fontSize: 12, color: S[500] }}>
                Cluster connected, no wedged workflows, success rate within target.
              </div>
            )}
          </div>
          <div style={{ fontSize: 10, color: S[400] }}>
            Checked {data.checkedAt ? new Date(data.checkedAt).toLocaleTimeString() : '—'}
          </div>
        </div>
      </div>

      {/* Stat grid */}
      <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(200px, 1fr))',
        gap: 12, marginBottom: 16 }}>
        <StatCard label="Cluster" accent={cluster.connected ? ACCENT.emerald : ACCENT.rose}
          icon={Network}
          value={cluster.connected ? 'Online' : 'Offline'}
          subtitle={cluster.connected
            ? `${cluster.rpcLatencyMs ?? '—'} ms · ${cluster.serverVersion || ''}`
            : (cluster.error || 'Unreachable')} />
        <StatCard label="In-flight workflows" accent={ACCENT.indigo || ACCENT.sky}
          icon={Activity} value={fmtN(wf.inFlight)}
          subtitle={`namespace: ${cluster.namespace || 'agentlets'}`} />
        <StatCard label="Wedged > 60min"
          accent={(wf.wedged || 0) > 0 ? ACCENT.rose : ACCENT.emerald}
          icon={(wf.wedged || 0) > 0 ? AlertTriangle : CheckCircle}
          value={fmtN(wf.wedged)}
          subtitle={(wf.wedged || 0) > 0
            ? 'Run terminate-wedged on Recovery tab' : 'Nothing stuck'} />
        <StatCard label="24h success rate"
          accent={successAccent(wf.last24hSuccessRate)}
          icon={CheckCircle}
          value={wf.last24hSuccessRate != null
            ? `${(wf.last24hSuccessRate * 100).toFixed(1)}%`
            : '—'}
          subtitle={`${fmtN(wf.last24hCompleted)} ok / ${fmtN(wf.last24hFailed)} failed`} />
        <StatCard label="24h P99 latency" accent={ACCENT.amber} icon={Gauge}
          value={fmtMs(wf.last24hP99Ms)}
          subtitle={`P50 ${fmtMs(wf.last24hP50Ms)} · P90 ${fmtMs(wf.last24hP90Ms)}`} />
      </div>

      {/* Recommended actions */}
      {posture.level !== 'healthy' && (
        <div className="acp-card" style={{
          background: '#fff', border: `1px solid ${pm.accent.border}`, borderRadius: 12,
          padding: '16px 20px',
        }}>
          <div style={{ fontSize: 11, fontWeight: 700, color: pm.accent.text,
            textTransform: 'uppercase', letterSpacing: '0.06em', marginBottom: 8 }}>
            Recommended actions
          </div>
          <ul style={{ margin: 0, paddingLeft: 18, fontSize: 13, color: S[700], lineHeight: 1.7 }}>
            {(wf.wedged || 0) > 0 && (
              <li>Open <strong>Recovery & Cleanup</strong> and run <code style={codeStyle}>terminate-wedged</code> to release {wf.wedged} stuck workflow{wf.wedged !== 1 ? 's' : ''} from the worker pool.</li>
            )}
            {!cluster.connected && (
              <li>Temporal OSS cluster unreachable. Check that the <code style={codeStyle}>temporal</code> container is running and <code style={codeStyle}>TEMPORAL_ADDRESS</code> matches its DNS name.</li>
            )}
            {wf.last24hSuccessRate != null && wf.last24hSuccessRate < 0.95 && (
              // ── 2026-05-04 fix ─────────────────────────────────────────
              // Old text said "Open Workflow Audit filtered to BLOCK".
              // That conflated two unrelated metrics:
              //   • last24hSuccessRate is computed from workflow_runs.status
              //     ('completed' / 'failed' / 'cancelled') — these are
              //     RUNTIME failures (timeouts, exceptions, crashed
              //     activities). See TemporalHealthService:184.
              //   • Workflow Audit filters on governance DECISION
              //     (ALLOW/WARN/BLOCK) — pre-flight policy decisions.
              // A BLOCK never produces a workflow_runs row at all, and a
              // runtime failure never produces a BLOCK audit entry, so
              // pointing the user at "BLOCK" to find runtime failures
              // shows an empty page (the user-reported bug). The bullet
              // now describes what "failed" actually means and links to
              // Workflow Runtime where in-flight + recent state lives.
              <li>
                Success rate below target — {fmtN(wf.last24hFailed)} of{' '}
                {fmtN((wf.last24hCompleted || 0) + (wf.last24hFailed || 0))}{' '}
                workflow run(s) failed at runtime in the last 24h (timeouts,
                exceptions, or cancellations). Open{' '}
                <button
                  onClick={() => setActiveTab && setActiveTab('workflow-runtime')}
                  style={{
                    background: 'none', border: 'none', padding: 0,
                    color: pm.accent.text, fontWeight: 700, cursor: 'pointer',
                    textDecoration: 'underline',
                  }}
                >
                  Workflow Runtime
                </button>{' '}
                to inspect in-flight runs, then drill into the per-workflow
                Detail page for full execution history. Note: this is
                separate from policy <code style={codeStyle}>BLOCK</code>{' '}
                decisions — those appear in <strong>Workflow Audit</strong>{' '}
                and represent pre-flight gate refusals, not runtime failures.
              </li>
            )}
          </ul>
        </div>
      )}
    </div>
  );
}

function successAccent(rate) {
  if (rate == null) return ACCENT.slate;
  if (rate >= 0.95) return ACCENT.emerald;
  if (rate >= 0.80) return ACCENT.amber;
  return ACCENT.rose;
}

/* ══════════════════════════════════════════════════════════════════════════════
   WORKFLOW RUNTIME TAB — in-flight list + per-row terminate
   ══════════════════════════════════════════════════════════════════════════════ */

function WorkflowRuntimeTab() {
  const [onlyWedged, setOnlyWedged] = useState(false);
  const [minAgeMinutes, setMinAgeMinutes] = useState(30);
  const [data, setData] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [terminating, setTerminating] = useState(null);
  const [confirmTerminate, setConfirmTerminate] = useState(null);

  // Bulk-selection state for "Terminate Selected" action.
  // Keyed by `${workflowId}:${runId}` to disambiguate retried runs.
  const [selected, setSelected] = useState(() => new Set());
  const [confirmBulk, setConfirmBulk] = useState(false);
  const [bulkRunning, setBulkRunning] = useState(false);
  const [bulkResult, setBulkResult] = useState(null);

  // "Terminate ALL Running" — paginated mass-purge across Temporal OSS, not just
  // the visible page of 200. Separate state so it doesn't collide with the
  // selection-based bulk path above.
  const [confirmAll, setConfirmAll] = useState(false);
  const [allRunning, setAllRunning] = useState(false);
  const [allResult, setAllResult] = useState(null);

  const rowKey = (w) => `${w.workflowId}:${w.runId}`;

  const reload = useCallback(async () => {
    setLoading(true); setError(null);
    try {
      const res = await api.getTemporalInFlight({ onlyWedged, minAgeMinutes, limit: 200 });
      if (res.ok) setData(Array.isArray(res.data) ? res.data : []);
      // G585 — apiFetch returns {ok:false, error:'...'} on non-2xx; the
      // previous res.data?.message was always undefined (res.data is only
      // set on success), so every server error printed the generic
      // "Failed to load" and hid the real reason. Read res.error first.
      else setError(res.error || res.data?.message || 'Failed to load');
    } catch (e) { setError(e.message); }
    finally { setLoading(false); }
  }, [onlyWedged, minAgeMinutes]);

  useEffect(() => { reload(); }, [reload]);

  // When the underlying data set changes, prune selection so we never carry
  // stale keys into the bulk request (e.g. rows we already terminated).
  useEffect(() => {
    setSelected((prev) => {
      if (prev.size === 0) return prev;
      const live = new Set(data.map(rowKey));
      let mutated = false;
      const next = new Set();
      prev.forEach((k) => { if (live.has(k)) next.add(k); else mutated = true; });
      return mutated ? next : prev;
    });
  }, [data]);

  const handleTerminate = async (wf) => {
    setTerminating(wf.runId);
    try {
      await api.terminateTemporalWorkflow({
        workflowId: wf.workflowId,
        runId: wf.runId,
        reason: `Terminated via Admin Center · ${new Date().toISOString()}`,
      });
      await reload();
    } finally {
      setTerminating(null);
      setConfirmTerminate(null);
    }
  };

  const toggleRow = (w) => {
    setSelected((prev) => {
      const k = rowKey(w);
      const next = new Set(prev);
      if (next.has(k)) next.delete(k); else next.add(k);
      return next;
    });
  };

  const allVisibleSelected = data.length > 0 && data.every((w) => selected.has(rowKey(w)));
  const someVisibleSelected = !allVisibleSelected && data.some((w) => selected.has(rowKey(w)));

  const toggleAllVisible = () => {
    setSelected((prev) => {
      if (allVisibleSelected) {
        // Deselect all currently visible rows; preserve any out-of-view selections.
        const next = new Set(prev);
        data.forEach((w) => next.delete(rowKey(w)));
        return next;
      }
      const next = new Set(prev);
      data.forEach((w) => next.add(rowKey(w)));
      return next;
    });
  };

  const clearSelection = () => setSelected(new Set());

  const handleBulkTerminate = async () => {
    const targets = data
      .filter((w) => selected.has(rowKey(w)))
      .map((w) => ({ workflowId: w.workflowId, runId: w.runId }));
    if (targets.length === 0) return;
    setBulkRunning(true);
    try {
      const res = await api.terminateBulkTemporalWorkflows({
        targets,
        reason: `Bulk-terminated (selection) via Admin Center · ${new Date().toISOString()}`,
      });
      setBulkResult(res.ok ? res.data : { error: res.data?.message || 'Failed' });
      clearSelection();
      await reload();
    } finally {
      setBulkRunning(false);
      setConfirmBulk(false);
    }
  };

  const selectedCount = selected.size;

  // Live progress for the chunked mass-terminate so the operator sees the
  // counter ticking up rather than staring at a frozen "Terminating ALL…" state.
  const [allProgress, setAllProgress] = useState(null);

  // Mass-terminate runs in chunks of 500 client-side. Each chunk is a single
  // HTTP call that finishes in 5-10s — well inside any proxy timeout. We loop
  // until the server reports a chunk that scanned 0 (queue is empty) or until
  // we hit a safety wall (50 chunks = 25k workflows in one button press).
  const handleTerminateAll = async () => {
    const CHUNK = 500;
    const MAX_CHUNKS = 50;
    setAllRunning(true);
    setAllProgress({ scanned: 0, terminated: 0, failed: 0, chunks: 0 });
    let totalScanned = 0, totalTerminated = 0, totalFailed = 0;
    let chunks = 0;
    let stoppedReason = null;
    let lastError = null;
    try {
      for (let i = 0; i < MAX_CHUNKS; i++) {
        const res = await api.terminateAllTemporalWorkflows({
          reason: `Mass-terminate (ALL running) via Admin Center · ${new Date().toISOString()}`,
          maxBatch: CHUNK,
        });
        if (!res.ok) {
          lastError = res.data?.message || `HTTP ${res.status || '?'}`;
          stoppedReason = 'error';
          break;
        }
        const d = res.data || {};
        const scanned = Number(d.scanned || 0);
        totalScanned += scanned;
        totalTerminated += Number(d.terminated || 0);
        totalFailed += Number(d.failed || 0);
        chunks++;
        setAllProgress({
          scanned: totalScanned,
          terminated: totalTerminated,
          failed: totalFailed,
          chunks,
        });
        // Empty page = queue is drained.
        if (scanned === 0) { stoppedReason = 'drained'; break; }
        // Otherwise keep going — the next pagination cursor restarts from the
        // top of the still-open list, which by now has fewer entries.
      }
      if (!stoppedReason) stoppedReason = 'max_chunks';

      setAllResult(lastError
        ? { error: lastError, scanned: totalScanned, terminated: totalTerminated,
            failed: totalFailed, chunks }
        : { scanned: totalScanned, terminated: totalTerminated, failed: totalFailed,
            chunks, stoppedReason });
      clearSelection();
      await reload();
    } finally {
      setAllRunning(false);
      setAllProgress(null);
      setConfirmAll(false);
    }
  };

  return (
    <div style={{ padding: '20px 28px 32px' }}>
      {/* Filter bar */}
      <div style={{
        background: '#fff', border: `1px solid ${S[200]}`, borderRadius: 12,
        padding: '12px 14px', display: 'flex', alignItems: 'center', gap: 14,
        flexWrap: 'wrap', marginBottom: 16,
      }}>
        <label style={filterLabel}>
          <input type="checkbox" checked={onlyWedged}
            onChange={(e) => setOnlyWedged(e.target.checked)}
            style={{ marginRight: 6 }} />
          Only wedged
        </label>
        <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
          <span style={fLabelTxt}>Min age</span>
          <select value={minAgeMinutes} onChange={(e) => setMinAgeMinutes(parseInt(e.target.value, 10))}
            style={selectStyle}>
            <option value={5}>5 min</option>
            <option value={15}>15 min</option>
            <option value={30}>30 min</option>
            <option value={60}>60 min</option>
            <option value={180}>3 hr</option>
            <option value={1440}>24 hr</option>
          </select>
        </div>
        <div style={{ marginLeft: 'auto', fontSize: 11, color: S[500] }}>
          {data.length} workflow{data.length !== 1 ? 's' : ''}
        </div>
        <button onClick={reload} disabled={loading} className="acp-btn-ghost" style={{
          ...btnBase, padding: '6px 12px', fontSize: 11, fontWeight: 600,
          border: `1px solid ${S[200]}`, background: '#fff', color: S[700],
        }}>
          <RefreshCw size={12} style={{ animation: loading ? 'acpSpin 1s linear infinite' : 'none' }} />
          Refresh
        </button>
        {/* Namespace-wide mass-purge. Distinct from "Terminate selected" because
            it paginates across Temporal OSS — not just the 200 currently visible. */}
        <button
          onClick={() => setConfirmAll(true)}
          disabled={allRunning}
          title="Paginate Temporal OSS and terminate every in-flight workflow in the namespace"
          style={{
            ...btnBase, padding: '6px 12px', fontSize: 11, fontWeight: 700,
            border: `1px solid ${ACCENT.rose.icon}`, background: '#fff',
            color: ACCENT.rose.icon,
          }}>
          <Trash2 size={12} />
          {allRunning ? 'Terminating ALL…' : 'Terminate ALL Running'}
        </button>
      </div>

      {error && <BodyError msg={error} onRetry={reload} />}

      {/* Bulk-action bar — appears whenever 1+ rows are selected.
          Sits between the filter bar and the table so it doesn't shift
          column layout, and is impossible to miss before clicking. */}
      {selectedCount > 0 && (
        <div style={{
          background: ACCENT.rose.bg, border: `1px solid ${ACCENT.rose.border}`,
          borderRadius: 12, padding: '10px 14px',
          display: 'flex', alignItems: 'center', gap: 12,
          marginBottom: 12,
        }}>
          <Trash2 size={14} style={{ color: ACCENT.rose.icon }} />
          <span style={{ fontSize: 12, fontWeight: 600, color: ACCENT.rose.text }}>
            {selectedCount} workflow{selectedCount !== 1 ? 's' : ''} selected
          </span>
          <button onClick={clearSelection} style={{
            ...btnBase, padding: '4px 10px', fontSize: 11, fontWeight: 600,
            background: '#fff', border: `1px solid ${S[200]}`, color: S[700],
          }}>
            Clear
          </button>
          <div style={{ marginLeft: 'auto', display: 'flex', gap: 8 }}>
            <button
              onClick={() => setConfirmBulk(true)}
              disabled={bulkRunning}
              style={{
                ...btnBase, padding: '6px 14px', fontSize: 12, fontWeight: 700,
                background: ACCENT.rose.icon, color: '#fff', border: 'none',
              }}>
              <Trash2 size={12} />
              {bulkRunning ? 'Terminating…' : `Terminate selected (${selectedCount})`}
            </button>
          </div>
        </div>
      )}

      {/* Live progress while mass-terminate is iterating chunks. Without this
          the user stares at a spinner for minutes with no signal that work is
          happening — same UX failure that made them think it was broken. */}
      {allRunning && allProgress && (
        <div style={{
          background: ACCENT.amber.bg, border: `1px solid ${ACCENT.amber.border}`,
          borderRadius: 8, padding: '10px 14px', marginBottom: 12,
          fontSize: 12, color: ACCENT.amber.text,
          display: 'flex', alignItems: 'center', gap: 10, flexWrap: 'wrap',
        }}>
          <RefreshCw size={12} style={{ animation: 'acpSpin 1s linear infinite' }} />
          <span style={{ fontWeight: 700 }}>Mass-terminate in progress…</span>
          <span>
            chunk {allProgress.chunks} · scanned {allProgress.scanned} ·
            {' '}terminated {allProgress.terminated} · failed {allProgress.failed}
          </span>
        </div>
      )}

      {/* Mass-purge result toast — distinct shape from bulk-selection because
          this can take 10+ seconds and the operator wants the full numbers. */}
      {allResult && !confirmAll && !allRunning && (
        <div style={{
          background: allResult.error ? ACCENT.rose.bg : ACCENT.emerald.bg,
          border: `1px solid ${allResult.error ? ACCENT.rose.border : ACCENT.emerald.border}`,
          borderRadius: 8, padding: '10px 14px', marginBottom: 12,
          fontSize: 12, color: allResult.error ? ACCENT.rose.text : ACCENT.emerald.text,
          display: 'flex', alignItems: 'center', gap: 10, flexWrap: 'wrap',
        }}>
          <span style={{ fontWeight: 700 }}>
            {allResult.error ? 'Mass-terminate stopped early:' : 'Namespace purge complete:'}
          </span>
          <span>
            {`scanned ${allResult.scanned || 0} · terminated ${allResult.terminated || 0} · ` +
             `failed ${allResult.failed || 0} · ${allResult.chunks || 0} chunk` +
             ((allResult.chunks || 0) === 1 ? '' : 's') +
             (allResult.error ? ` · error: ${allResult.error}` : '') +
             (allResult.stoppedReason === 'max_chunks'
                ? ' · MAX CHUNKS HIT (click again to continue)'
                : '')}
          </span>
          <button onClick={() => setAllResult(null)} style={{
            ...btnBase, marginLeft: 'auto', padding: '2px 8px', fontSize: 10,
            background: 'transparent', border: 'none', color: 'inherit', fontWeight: 600,
          }}>Dismiss</button>
        </div>
      )}

      {/* Last bulk-result toast — auto-dismisses on next refresh */}
      {bulkResult && !confirmBulk && (
        <div style={{
          background: bulkResult.error ? ACCENT.rose.bg : ACCENT.emerald.bg,
          border: `1px solid ${bulkResult.error ? ACCENT.rose.border : ACCENT.emerald.border}`,
          borderRadius: 8, padding: '8px 12px', marginBottom: 12,
          fontSize: 11, color: bulkResult.error ? ACCENT.rose.text : ACCENT.emerald.text,
          display: 'flex', alignItems: 'center', gap: 8,
        }}>
          <span style={{ fontWeight: 700 }}>
            {bulkResult.error ? 'Error:' : 'Bulk terminate result:'}
          </span>
          <span>
            {bulkResult.error
              ? bulkResult.error
              : `requested ${bulkResult.requested} · terminated ${bulkResult.terminated} · failed ${bulkResult.failed}`}
          </span>
          <button onClick={() => setBulkResult(null)} style={{
            ...btnBase, marginLeft: 'auto', padding: '2px 8px', fontSize: 10,
            background: 'transparent', border: 'none', color: 'inherit', fontWeight: 600,
          }}>Dismiss</button>
        </div>
      )}

      {!error && data.length === 0 && !loading && (
        <BodyEmpty msg={onlyWedged
          ? 'No workflows older than the threshold — runtime is clean.'
          : 'No in-flight workflows. Trigger a run to populate this view.'} />
      )}

      {data.length > 0 && (
        <div style={{
          background: '#fff', border: `1px solid ${S[200]}`, borderRadius: 12,
          overflow: 'hidden',
        }}>
          <div style={{
            display: 'grid',
            gridTemplateColumns: '36px minmax(200px,1.5fr) minmax(180px,1.2fr) 90px 90px 110px 110px',
            background: S[50], borderBottom: `1px solid ${S[200]}`,
            fontSize: 9, fontWeight: 700, color: S[500],
            textTransform: 'uppercase', letterSpacing: '0.06em',
          }}>
            <div style={{ ...hdrCellAcp, padding: '10px 0', justifyContent: 'center' }}>
              <input
                type="checkbox"
                aria-label="Select all visible workflows"
                checked={allVisibleSelected}
                ref={(el) => { if (el) el.indeterminate = someVisibleSelected; }}
                onChange={toggleAllVisible}
                style={{ cursor: 'pointer' }}
              />
            </div>
            <div style={hdrCellAcp}>Workflow ID</div>
            <div style={hdrCellAcp}>Type</div>
            <div style={{ ...hdrCellAcp, textAlign: 'right' }}>Age</div>
            <div style={{ ...hdrCellAcp, textAlign: 'right' }}>History</div>
            <div style={{ ...hdrCellAcp, textAlign: 'center' }}>Status</div>
            <div style={{ ...hdrCellAcp, textAlign: 'right' }}>Actions</div>
          </div>
          {data.map((w) => {
            const k = rowKey(w);
            const isSel = selected.has(k);
            return (
            <div key={k} className="acp-row" style={{
              display: 'grid',
              gridTemplateColumns: '36px minmax(200px,1.5fr) minmax(180px,1.2fr) 90px 90px 110px 110px',
              padding: 0, borderBottom: `1px solid ${S[100]}`, alignItems: 'center',
              fontSize: 12,
              background: isSel ? ACCENT.rose.bg : 'transparent',
            }}>
              <div style={{ ...cellAcp, padding: '10px 0',
                display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
                <input
                  type="checkbox"
                  aria-label={`Select workflow ${w.workflowId}`}
                  checked={isSel}
                  onChange={() => toggleRow(w)}
                  style={{ cursor: 'pointer' }}
                />
              </div>
              <div style={cellAcp}>
                <div style={{ fontFamily: "'IBM Plex Mono', monospace",
                  color: S[800], overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                  {w.workflowId}
                </div>
                <div style={{ fontSize: 10, color: S[500], marginTop: 2,
                  fontFamily: "'IBM Plex Mono', monospace" }}>
                  {String(w.runId).slice(0, 24)}…
                </div>
              </div>
              <div style={cellAcp}>{w.workflowType}</div>
              <div style={{ ...cellAcp, textAlign: 'right',
                color: (w.ageMinutes || 0) > 60 ? ACCENT.rose.text : S[700],
                fontVariantNumeric: 'tabular-nums' }}>
                {fmtAge(w.ageMinutes)}
              </div>
              <div style={{ ...cellAcp, textAlign: 'right',
                fontFamily: "'IBM Plex Mono', monospace",
                fontVariantNumeric: 'tabular-nums', color: S[600] }}>
                {fmtN(w.historyLength)}
              </div>
              <div style={{ ...cellAcp, textAlign: 'center' }}>
                <span style={statusPill(w.status)}>{statusLabel(w.status)}</span>
              </div>
              <div style={{ ...cellAcp, textAlign: 'right' }}>
                <button
                  onClick={() => setConfirmTerminate(w)}
                  disabled={terminating === w.runId}
                  className="acp-btn-danger"
                  style={{
                    ...btnBase, padding: '5px 11px', fontSize: 11, fontWeight: 600,
                    background: ACCENT.rose.bg, border: `1px solid ${ACCENT.rose.border}`,
                    color: ACCENT.rose.text,
                  }}>
                  <Trash2 size={11} />
                  {terminating === w.runId ? 'Terminating…' : 'Terminate'}
                </button>
              </div>
            </div>
            );
          })}
        </div>
      )}

      {/* Mass-purge confirm modal — extra-stern wording because this is
          namespace-wide and not bounded by the visible 200 rows. */}
      {confirmAll && (
        <Modal isOpen title="Terminate ALL in-flight workflows in this namespace?"
               onClose={() => setConfirmAll(false)}>
          <div style={{ fontSize: 13, color: S[700], lineHeight: 1.6 }}>
            <div style={{
              padding: '10px 14px', background: ACCENT.rose.bg,
              border: `1px solid ${ACCENT.rose.border}`, borderRadius: 8,
              marginBottom: 12, fontSize: 12, color: ACCENT.rose.text,
              fontWeight: 600,
            }}>
              ⚠ This kills every currently-running workflow in the namespace —
              not just the {data.length} visible on this page.
            </div>
            <p>
              The server will paginate Temporal OSS's open-workflow list and call
              terminate on each, up to 50,000 in one pass. If your backlog is
              larger than that you'll see "CEILING HIT" and need to run it again.
            </p>
            <p style={{ fontSize: 11, color: S[500], marginTop: 12 }}>
              Each termination is recorded on the workflow history with the
              reason "Mass-terminate (ALL running) via Admin Center" — audits
              later will show why.
            </p>
            <div style={{ display: 'flex', gap: 8, justifyContent: 'flex-end', marginTop: 16 }}>
              <button onClick={() => setConfirmAll(false)} disabled={allRunning} style={{
                ...btnBase, padding: '8px 16px', fontSize: 12, fontWeight: 600,
                border: `1px solid ${S[200]}`, background: '#fff', color: S[700],
              }}>Cancel</button>
              <button onClick={handleTerminateAll} disabled={allRunning} style={{
                ...btnBase, padding: '8px 16px', fontSize: 12, fontWeight: 700,
                background: ACCENT.rose.icon, color: '#fff', border: 'none',
              }}>
                {allRunning ? 'Terminating ALL…' : 'Yes, terminate ALL running'}
              </button>
            </div>
          </div>
        </Modal>
      )}

      {/* Bulk confirm modal */}
      {confirmBulk && (
        <Modal isOpen title={`Terminate ${selectedCount} selected workflow${selectedCount !== 1 ? 's' : ''}?`}
               onClose={() => setConfirmBulk(false)}>
          <div style={{ fontSize: 13, color: S[700], lineHeight: 1.6 }}>
            <p>
              This will terminate <strong>{selectedCount}</strong> workflow execution{selectedCount !== 1 ? 's' : ''} immediately.
              In-flight activities may continue briefly until they fail their next heartbeat.
            </p>
            <p style={{ fontSize: 11, color: S[500], marginTop: 12 }}>
              Each termination is recorded on the workflow history with the reason
              "Bulk-terminated (selection) via Admin Center" — audits later will show why.
            </p>
            <div style={{ display: 'flex', gap: 8, justifyContent: 'flex-end', marginTop: 16 }}>
              <button onClick={() => setConfirmBulk(false)} disabled={bulkRunning} style={{
                ...btnBase, padding: '8px 16px', fontSize: 12, fontWeight: 600,
                border: `1px solid ${S[200]}`, background: '#fff', color: S[700],
              }}>Cancel</button>
              <button onClick={handleBulkTerminate} disabled={bulkRunning} style={{
                ...btnBase, padding: '8px 16px', fontSize: 12, fontWeight: 700,
                background: ACCENT.rose.icon, color: '#fff', border: 'none',
              }}>
                {bulkRunning ? 'Terminating…' : `Yes, terminate ${selectedCount}`}
              </button>
            </div>
          </div>
        </Modal>
      )}

      {/* Confirm terminate modal */}
      {confirmTerminate && (
        <Modal isOpen={!!confirmTerminate} title="Terminate workflow?" onClose={() => setConfirmTerminate(null)}>
          <div style={{ fontSize: 13, color: S[700], lineHeight: 1.6 }}>
            <p>This will terminate the workflow execution immediately. Activities in flight may continue briefly until they fail their next heartbeat.</p>
            <div style={{
              padding: '10px 14px', background: S[50], border: `1px solid ${S[200]}`,
              borderRadius: 8, marginTop: 10, fontSize: 11,
              fontFamily: "'IBM Plex Mono', monospace", color: S[800],
            }}>
              {confirmTerminate.workflowId}<br/>
              <span style={{ color: S[500] }}>type:</span> {confirmTerminate.workflowType}<br/>
              <span style={{ color: S[500] }}>age:</span> {fmtAge(confirmTerminate.ageMinutes)}
            </div>
            <p style={{ fontSize: 11, color: S[500], marginTop: 12 }}>
              This action is recorded on the workflow history with reason
              "Terminated via Admin Center" — audits later will show why.
            </p>
            <div style={{ display: 'flex', gap: 8, justifyContent: 'flex-end', marginTop: 16 }}>
              <button onClick={() => setConfirmTerminate(null)} style={{
                ...btnBase, padding: '8px 16px', fontSize: 12, fontWeight: 600,
                border: `1px solid ${S[200]}`, background: '#fff', color: S[700],
              }}>Cancel</button>
              <button onClick={() => handleTerminate(confirmTerminate)} style={{
                ...btnBase, padding: '8px 16px', fontSize: 12, fontWeight: 700,
                background: ACCENT.rose.icon, color: '#fff', border: 'none',
              }}>Terminate now</button>
            </div>
          </div>
        </Modal>
      )}
    </div>
  );
}

/* ══════════════════════════════════════════════════════════════════════════════
   WORKER TUNING TAB — read-only effective config
   ══════════════════════════════════════════════════════════════════════════════ */

function WorkerTuningTab() {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  const reload = useCallback(async () => {
    setLoading(true); setError(null);
    try {
      const res = await api.getTemporalWorkerConfig();
      if (res.ok) setData(res.data);
      // G585 — apiFetch returns {ok:false, error:'...'} on non-2xx; the
      // previous res.data?.message was always undefined (res.data is only
      // set on success), so every server error printed the generic
      // "Failed to load" and hid the real reason. Read res.error first.
      else setError(res.error || res.data?.message || 'Failed to load');
    } catch (e) { setError(e.message); }
    finally { setLoading(false); }
  }, []);

  useEffect(() => { reload(); }, [reload]);

  if (loading && !data) return <BodyLoading msg="Loading worker config…" />;
  if (error) return <BodyError msg={error} onRetry={reload} />;
  if (!data) return null;

  return (
    <div style={{ padding: '20px 28px 32px' }}>
      <div style={{
        padding: '12px 16px', background: ACCENT.sky.bg,
        border: `1px solid ${ACCENT.sky.border}`, borderRadius: 10, marginBottom: 16,
        display: 'flex', gap: 10, alignItems: 'flex-start',
      }}>
        <Info size={14} color={ACCENT.sky.icon} style={{ marginTop: 2, flexShrink: 0 }} />
        <div style={{ fontSize: 12, color: ACCENT.sky.text, lineHeight: 1.5 }}>
          <strong>Read-only.</strong> These values reflect the current worker process.
          Edits go via Spring properties / env vars in the deploy config — runtime mutation
          of worker tuning is deliberately not exposed (it's a footgun: cache size changes
          mid-flight invalidate stickiness; pool size changes can starve in-flight tasks).
          See <a href="/docs/TEMPORAL_OPS_RUNBOOK.md" target="_blank" rel="noreferrer"
            style={{ color: ACCENT.sky.text, fontWeight: 600 }}>
            Temporal OSS Ops Runbook §5
          </a> for the override path.
        </div>
      </div>

      <ConfigPanel title="gRPC + Connectivity" icon={Network}
        accent={ACCENT.indigo || ACCENT.sky}
        rows={[
          ['Max inbound message size', mb(data.grpc?.maxInboundMessageSizeMb), 'temporal.grpc.max-inbound-message-size-mb'],
          ['Keep-alive interval', s(data.grpc?.keepAliveSeconds), 'temporal.grpc.keep-alive-seconds'],
          ['Keep-alive timeout', s(data.grpc?.keepAliveTimeoutSeconds), 'temporal.grpc.keep-alive-timeout-seconds'],
          ['Long-poll timeout', s(data.grpc?.longPollTimeoutSeconds), 'temporal.grpc.long-poll-timeout-seconds'],
        ]} />

      <ConfigPanel title="Worker Factory (cross-worker)" icon={Cog}
        accent={ACCENT.violet}
        rows={[
          ['Workflow cache size', fmtN(data.workerFactory?.workflowCacheSize), 'temporal.worker-factory.workflow-cache-size'],
          ['Workflow host thread count', data.workerFactory?.workflowHostThreadCount === 0
            ? 'auto (4×CPU)' : fmtN(data.workerFactory?.workflowHostThreadCount),
            'temporal.worker-factory.workflow-host-thread-count'],
        ]} />

      <ConfigPanel title="Per-Worker Concurrency" icon={Cpu}
        accent={ACCENT.emerald}
        rows={[
          ['Max concurrent activities', fmtN(data.worker?.maxConcurrentActivityExecutions), 'temporal.worker.max-concurrent-activity-executions'],
          ['Max concurrent workflow tasks', fmtN(data.worker?.maxConcurrentWorkflowTaskExecutions), 'temporal.worker.max-concurrent-workflow-task-executions'],
          ['Max concurrent local activities', fmtN(data.worker?.maxConcurrentLocalActivityExecutions), 'temporal.worker.max-concurrent-local-activity-executions'],
          ['Workflow poll thread count', fmtN(data.worker?.workflowPollThreadCount), 'temporal.worker.workflow-poll-thread-count'],
          ['Activity poll thread count', fmtN(data.worker?.activityPollThreadCount), 'temporal.worker.activity-poll-thread-count'],
          ['Deadlock detection timeout', s(data.worker?.defaultDeadlockDetectionTimeoutSeconds),
            'temporal.worker.default-deadlock-detection-timeout-seconds'],
        ]} />

      <ConfigPanel title="Server / Namespace" icon={Database}
        accent={ACCENT.slate}
        rows={[
          ['Temporal OSS server address', data.server?.address, 'temporal.server.address'],
          ['Namespace', data.server?.namespace, 'temporal.namespace'],
        ]} />

      {/* Property source layering — helps debug "why is this value what it is?" */}
      {data.propertySources && (
        <details style={{ marginTop: 16 }}>
          <summary style={{ fontSize: 11, fontWeight: 600, color: S[500], cursor: 'pointer',
            padding: '8px 12px', background: S[50], borderRadius: 8 }}>
            Property source layering — debug "why is this value what it is?"
          </summary>
          <div style={{
            background: '#fff', border: `1px solid ${S[200]}`, borderRadius: 8,
            padding: 12, marginTop: 8, fontSize: 11,
            fontFamily: "'IBM Plex Mono', monospace", color: S[700],
          }}>
            {data.propertySources.map((ps, i) => (
              <div key={i} style={{ padding: '3px 0', borderBottom: i < data.propertySources.length - 1 ? `1px solid ${S[100]}` : 'none' }}>
                <span style={{ color: S[400] }}>{i + 1}.</span> {ps.name}
                {ps.temporalKeyCount > 0 && (
                  <span style={{ color: ACCENT.violet.text, marginLeft: 8 }}>
                    ({ps.temporalKeyCount} temporal.* keys)
                  </span>
                )}
              </div>
            ))}
          </div>
        </details>
      )}
    </div>
  );
}

function ConfigPanel({ title, icon: Icon, accent, rows }) {
  return (
    <div className="acp-card" style={{
      background: '#fff', border: `1px solid ${S[200]}`, borderRadius: 12,
      marginBottom: 14, overflow: 'hidden',
    }}>
      <div style={{
        padding: '12px 16px', background: accent.bg,
        borderBottom: `1px solid ${accent.border}`,
        display: 'flex', alignItems: 'center', gap: 8,
      }}>
        <Icon size={14} color={accent.icon} />
        <span style={{ fontSize: 11, fontWeight: 700, color: accent.text,
          textTransform: 'uppercase', letterSpacing: '0.06em' }}>{title}</span>
      </div>
      <div>
        {rows.map(([label, value, envKey]) => (
          <div key={label} style={{
            padding: '10px 16px', display: 'grid',
            gridTemplateColumns: 'minmax(220px, 1.4fr) minmax(120px, 0.8fr) minmax(220px, 1.6fr)',
            gap: 12, fontSize: 12, alignItems: 'center',
            borderBottom: `1px solid ${S[100]}`,
          }}>
            <div style={{ color: S[700], fontWeight: 500 }}>{label}</div>
            <div style={{
              fontFamily: "'IBM Plex Mono', monospace", color: S[900], fontWeight: 600,
              fontVariantNumeric: 'tabular-nums',
            }}>
              {value ?? '—'}
            </div>
            <div style={{ ...codeStyle, fontSize: 10, color: S[500],
              overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
              {envKey}
            </div>
          </div>
        ))}
      </div>
    </div>
  );
}

/* ══════════════════════════════════════════════════════════════════════════════
   MANAGED SKILLS TAB — Phase 7 (Auto-Skill Lifecycle)
   ─────────────────────────────────────────────────────────────────────────────
   Read-only inventory of every wizard-owned policy skill on the platform.
   These are skills with auto_generated=true, created by the unified policy
   assignment wizard's auto-create path. Operators see them here so they
   know not to hand-edit policy_config on these skills — the wizard owns
   them and re-runs replace state.
   ══════════════════════════════════════════════════════════════════════════════ */

function ManagedSkillsTab() {
  const [skills, setSkills] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [filter, setFilter] = useState('');

  const load = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      // Scope to wizard-owned policy skills only. Without this kind filter,
      // additional auto-generated kinds (e.g. AUTO_RUNBOOK_BINDING from Auto
      // Create) would leak into this read-only "wizard-managed" panel and
      // break the "re-run the wizard, not here" semantics.
      // See AUTO_CREATE_PRODUCTION_INTEGRATION_PLAN.md §3.2.
      const res = await api.listManagedSkills({ skillKind: 'AUTO_FRAMEWORK_BINDING' });
      if (res.ok) setSkills(Array.isArray(res.data) ? res.data : []);
      else setError(res.error || 'Failed to load managed skills');
    } catch (e) { setError(e.message); }
    finally { setLoading(false); }
  }, []);

  useEffect(() => { load(); }, [load]);

  const filtered = filter
    ? skills.filter(s => {
        const q = filter.toLowerCase();
        return (s.name || '').toLowerCase().includes(q)
            || (s.skillKind || '').toLowerCase().includes(q)
            || (s.description || '').toLowerCase().includes(q);
      })
    : skills;

  return (
    <div style={{ padding: '0 24px 24px' }}>
      <div style={{
        display: 'flex', alignItems: 'center', gap: 12, marginBottom: 16,
        padding: '14px 16px', borderRadius: 10,
        background: '#eff6ff', border: '1px solid #bfdbfe',
      }}>
        <Sparkles size={16} color="#1e40af" />
        <div style={{ flex: 1, fontSize: 12, color: '#1e40af', lineHeight: 1.5 }}>
          <strong>Wizard-managed skills.</strong> These were auto-created by the unified
          policy assignment wizard from a framework binding. Don't hand-edit
          {' '}<code style={{ fontFamily: 'ui-monospace, monospace', fontSize: 11 }}>policy_config</code>
          {' '}— re-run the wizard to update.
        </div>
        <button onClick={load} disabled={loading} style={{
          ...btnBase, padding: '6px 12px',
          border: `1px solid ${S[200]}`, background: '#fff', color: S[600], fontSize: 12,
        }}>
          <RefreshCw size={12} style={loading ? { animation: 'acpSpin 1s linear infinite' } : {}} />
          Refresh
        </button>
      </div>

      <div style={{ display: 'flex', alignItems: 'center', gap: 8, marginBottom: 12 }}>
        <input
          value={filter}
          onChange={e => setFilter(e.target.value)}
          placeholder="Filter by name, kind, or description…"
          style={{
            flex: 1, padding: '8px 12px', borderRadius: 8,
            border: `1px solid ${S[200]}`, fontSize: 13, outline: 'none',
            fontFamily: 'inherit',
          }}
        />
        <span style={{ fontSize: 12, color: S[500] }}>
          {filtered.length} of {skills.length}
        </span>
      </div>

      {error && (
        <div style={{
          padding: '10px 14px', borderRadius: 8, background: '#fef2f2',
          border: '1px solid #fecaca', color: '#991b1b', fontSize: 13, marginBottom: 12,
        }}>{error}</div>
      )}

      {loading ? (
        <div style={{ textAlign: 'center', padding: 32, color: S[400] }}>
          <Loader size={20} style={{ animation: 'acpSpin 1s linear infinite' }} />
        </div>
      ) : filtered.length === 0 ? (
        <div style={{ textAlign: 'center', padding: 48, color: S[500] }}>
          <Layers size={28} color={S[300]} style={{ marginBottom: 8 }} />
          <div style={{ fontSize: 14, fontWeight: 600, marginBottom: 4 }}>
            {skills.length === 0 ? 'No wizard-managed skills yet' : 'No matches'}
          </div>
          <div style={{ fontSize: 12 }}>
            {skills.length === 0
              ? 'Wizard-created skills appear here as soon as the first framework binding is activated.'
              : 'Try a different filter.'}
          </div>
        </div>
      ) : (
        <div style={{ borderRadius: 10, border: `1px solid ${S[200]}`, background: '#fff', overflow: 'hidden' }}>
          <div style={{
            display: 'grid', gridTemplateColumns: '1fr 160px 200px 140px',
            padding: '10px 14px', background: S[50], borderBottom: `1px solid ${S[200]}`,
            fontSize: 11, fontWeight: 700, color: S[600], textTransform: 'uppercase', letterSpacing: '0.06em',
          }}>
            <div>Skill</div>
            <div>Kind</div>
            <div>Framework Binding</div>
            <div>Created</div>
          </div>
          {filtered.map((s, i) => (
            <div key={s.id} style={{
              display: 'grid', gridTemplateColumns: '1fr 160px 200px 140px',
              padding: '12px 14px', borderBottom: i < filtered.length - 1 ? `1px solid ${S[100]}` : 'none',
              fontSize: 13, alignItems: 'center',
            }}>
              <div style={{ minWidth: 0 }}>
                <div style={{ fontWeight: 600, color: S[900], overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                  {s.name}
                </div>
                {s.description && (
                  <div style={{ fontSize: 11, color: S[500], marginTop: 2,
                    overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                    {s.description}
                  </div>
                )}
              </div>
              <div>
                <span style={{
                  display: 'inline-flex', padding: '2px 8px', borderRadius: 4,
                  fontSize: 10, fontWeight: 700, background: '#eff6ff', color: '#1e40af',
                  border: '1px solid #bfdbfe',
                }}>{s.skillKind || 'AUTO'}</span>
              </div>
              <code style={{ fontFamily: 'ui-monospace, monospace', fontSize: 11, color: S[600] }}
                title={String(s.frameworkBindingId || '')}>
                {s.frameworkBindingId
                  ? String(s.frameworkBindingId).slice(0, 13) + '…'
                  : <span style={{ color: S[400], fontStyle: 'italic' }}>none</span>}
              </code>
              <span style={{ fontSize: 11, color: S[500] }}>
                {s.createdAt ? new Date(s.createdAt).toLocaleDateString() : '—'}
              </span>
            </div>
          ))}
        </div>
      )}
    </div>
  );
}

/* ══════════════════════════════════════════════════════════════════════════════
   RECOVERY & CLEANUP TAB — bulk operations
   ══════════════════════════════════════════════════════════════════════════════ */

function RecoveryTab() {
  const [minAge, setMinAge] = useState(60);
  const [previewCount, setPreviewCount] = useState(null);
  const [previewing, setPreviewing] = useState(false);
  const [confirming, setConfirming] = useState(false);
  const [running, setRunning] = useState(false);
  const [result, setResult] = useState(null);
  const [error, setError] = useState(null);

  const preview = async () => {
    setPreviewing(true); setError(null);
    try {
      const res = await api.getTemporalInFlight({ onlyWedged: true, minAgeMinutes: minAge, limit: 500 });
      if (res.ok) setPreviewCount(Array.isArray(res.data) ? res.data.length : 0);
      else setError(res.data?.message || 'Failed to preview');
    } catch (e) { setError(e.message); }
    finally { setPreviewing(false); }
  };

  const execute = async () => {
    setRunning(true); setError(null); setResult(null);
    try {
      const res = await api.terminateWedgedTemporalWorkflows({
        minAgeMinutes: minAge, limit: 500,
        reason: `Bulk-terminated via Admin Center · ${new Date().toISOString()}`,
      });
      if (res.ok) setResult(res.data);
      else setError(res.data?.message || 'Termination failed');
    } catch (e) { setError(e.message); }
    finally { setRunning(false); setConfirming(false); setPreviewCount(null); }
  };

  return (
    <div style={{ padding: '20px 28px 32px' }}>
      <div style={{
        padding: '14px 18px', background: ACCENT.amber.bg,
        border: `1px solid ${ACCENT.amber.border}`, borderRadius: 12, marginBottom: 16,
        display: 'flex', gap: 12, alignItems: 'flex-start',
      }}>
        <AlertCircle size={16} color={ACCENT.amber.icon} style={{ marginTop: 2, flexShrink: 0 }} />
        <div style={{ fontSize: 12, color: ACCENT.amber.text, lineHeight: 1.5 }}>
          <strong>Destructive operations.</strong> Buttons here terminate workflows
          permanently. Always preview the count first. Reasons are recorded on
          workflow history for audit. <strong>Super / Platform Admin only</strong>
          — Tenant Admins will see 403 from these actions.
        </div>
      </div>

      <div className="acp-card" style={{
        background: '#fff', border: `1px solid ${S[200]}`, borderRadius: 12, padding: 20,
        marginBottom: 16,
      }}>
        <div style={{ display: 'flex', alignItems: 'center', gap: 10, marginBottom: 4 }}>
          <Trash2 size={17} color={ACCENT.rose.icon} />
          <h3 style={{ margin: 0, fontSize: 15, fontWeight: 700, color: S[900] }}>
            Bulk-terminate wedged workflows
          </h3>
        </div>
        <p style={{ fontSize: 12, color: S[500], margin: '4px 0 16px 0', lineHeight: 1.5 }}>
          Lists every in-flight workflow older than the threshold and terminates
          them in one shot. Use after a deploy that broke replay determinism —
          see Temporal OSS Ops Runbook §2.
        </p>

        <div style={{ display: 'flex', alignItems: 'center', gap: 12, marginBottom: 14 }}>
          <span style={fLabelTxt}>Wedge threshold</span>
          <select value={minAge} onChange={(e) => { setMinAge(parseInt(e.target.value, 10)); setPreviewCount(null); }}
            style={selectStyle}>
            <option value={15}>15 minutes</option>
            <option value={30}>30 minutes</option>
            <option value={60}>60 minutes</option>
            <option value={180}>3 hours</option>
            <option value={1440}>24 hours</option>
          </select>
          <button onClick={preview} disabled={previewing} style={{
            ...btnBase, padding: '7px 14px', fontSize: 12, fontWeight: 600,
            border: `1px solid ${S[200]}`, background: '#fff', color: S[700],
          }}>
            {previewing ? <Loader size={12} style={{ animation: 'acpSpin 1s linear infinite' }} /> : <Activity size={12} />}
            Preview count
          </button>
          {previewCount != null && (
            <span style={{
              fontSize: 12, fontWeight: 600,
              padding: '6px 12px', borderRadius: 6,
              background: previewCount === 0 ? ACCENT.emerald.bg : ACCENT.amber.bg,
              border: `1px solid ${previewCount === 0 ? ACCENT.emerald.border : ACCENT.amber.border}`,
              color: previewCount === 0 ? ACCENT.emerald.text : ACCENT.amber.text,
            }}>
              {previewCount} workflow{previewCount !== 1 ? 's' : ''} would be terminated
            </span>
          )}
        </div>

        <button
          onClick={() => setConfirming(true)}
          disabled={previewCount == null || previewCount === 0 || running}
          style={{
            ...btnBase, padding: '9px 18px', fontSize: 12, fontWeight: 700,
            background: previewCount && previewCount > 0 ? ACCENT.rose.icon : S[300],
            color: '#fff', border: 'none',
            cursor: previewCount && previewCount > 0 ? 'pointer' : 'not-allowed',
          }}>
          <Trash2 size={13} />
          {running ? 'Terminating…' : 'Terminate wedged workflows'}
        </button>

        {error && (
          <div style={{
            marginTop: 14, padding: '10px 14px', background: ACCENT.rose.bg,
            border: `1px solid ${ACCENT.rose.border}`, color: ACCENT.rose.text,
            borderRadius: 8, fontSize: 12,
          }}>
            {error}
          </div>
        )}

        {result && (
          <div style={{
            marginTop: 14, padding: '12px 14px', background: ACCENT.emerald.bg,
            border: `1px solid ${ACCENT.emerald.border}`, color: ACCENT.emerald.text,
            borderRadius: 8, fontSize: 12,
          }}>
            <div style={{ fontWeight: 700, marginBottom: 4 }}>
              ✓ Bulk termination complete
            </div>
            <div style={{ fontFamily: "'IBM Plex Mono', monospace" }}>
              scanned={result.scanned} · terminated={result.terminated} · failed={result.failed}
            </div>
          </div>
        )}
      </div>

      {/* Confirm modal */}
      {confirming && (
        <Modal isOpen title="Bulk-terminate confirmation" onClose={() => setConfirming(false)}>
          <div style={{ fontSize: 13, color: S[700], lineHeight: 1.6 }}>
            <p>
              You are about to terminate <strong>{previewCount}</strong> workflow
              {previewCount !== 1 ? 's' : ''} older than {minAge} minute{minAge !== 1 ? 's' : ''}.
              This is irreversible.
            </p>
            <p style={{ fontSize: 12, color: S[500] }}>
              Each workflow's history will record the termination with reason
              "Bulk-terminated via Admin Center" and the current timestamp.
            </p>
            <div style={{ display: 'flex', gap: 8, justifyContent: 'flex-end', marginTop: 16 }}>
              <button onClick={() => setConfirming(false)} style={{
                ...btnBase, padding: '8px 16px', fontSize: 12, fontWeight: 600,
                border: `1px solid ${S[200]}`, background: '#fff', color: S[700],
              }}>Cancel</button>
              <button onClick={execute} disabled={running} style={{
                ...btnBase, padding: '8px 16px', fontSize: 12, fontWeight: 700,
                background: ACCENT.rose.icon, color: '#fff', border: 'none',
              }}>
                {running ? 'Terminating…' : `Yes, terminate ${previewCount}`}
              </button>
            </div>
          </div>
        </Modal>
      )}

      {/* Documentation pointer */}
      <div className="acp-card" style={{
        background: '#fff', border: `1px solid ${S[200]}`, borderRadius: 12,
        padding: '16px 20px',
      }}>
        <div style={{ fontSize: 11, fontWeight: 700, color: S[500],
          textTransform: 'uppercase', letterSpacing: '0.06em', marginBottom: 8 }}>
          Recovery procedures
        </div>
        <ul style={{ margin: 0, paddingLeft: 18, fontSize: 13, color: S[700], lineHeight: 1.7 }}>
          <li><strong>Non-determinism after a deploy:</strong> Workflows started under old code can't replay against new code if activity calls were reordered. The runbook's Golden Rule — wrap every new activity call in <code style={codeStyle}>Workflow.getVersion(...)</code>.</li>
          <li><strong>gRPC RESOURCE_EXHAUSTED:</strong> Likely the staging Temporal OSS container is missing the <code style={codeStyle}>dynamicconfig.yaml</code> mount. Recreate the container with <code style={codeStyle}>docker compose up -d --force-recreate temporal</code>.</li>
          <li><strong>UnableToAcquireLock spam:</strong> Workflow lease wasn't released by the previous worker. Wait <code style={codeStyle}>workflowTaskTimeout</code> (30s) for the lease to expire — the worker will retry automatically.</li>
          <li><strong>High history length (>10k events):</strong> Workflow accumulating too much state. Use <code style={codeStyle}>Workflow.continueAsNew(...)</code> at a natural checkpoint.</li>
        </ul>
      </div>
    </div>
  );
}

/* ══════════════════════════════════════════════════════════════════════════════
   SHARED COMPONENTS
   ══════════════════════════════════════════════════════════════════════════════ */

function StatCard({ label, value, accent, icon: Icon, subtitle }) {
  const a = accent || ACCENT.slate;
  return (
    <div className="acp-card" style={{
      background: '#fff', border: `1px solid ${S[200]}`, borderRadius: 12,
      padding: '16px 18px', position: 'relative', overflow: 'hidden',
    }}>
      <div style={{ position: 'absolute', top: 0, left: 0, right: 0, height: 3,
        background: a.icon, opacity: 0.15 }} />
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start' }}>
        <div>
          <div style={{
            fontSize: 9, fontWeight: 700, color: a.text, textTransform: 'uppercase',
            letterSpacing: '0.08em', marginBottom: 6,
          }}>{label}</div>
          <div style={{
            fontSize: 22, fontWeight: 800, color: S[900], lineHeight: 1.05,
            letterSpacing: '-0.02em', fontVariantNumeric: 'tabular-nums',
          }}>{value}</div>
          {subtitle && <div style={{ fontSize: 11, color: S[500], marginTop: 4, lineHeight: 1.3 }}>{subtitle}</div>}
        </div>
        <div style={{
          width: 32, height: 32, borderRadius: 8, background: a.bg,
          border: `1px solid ${a.border}`, flexShrink: 0,
          display: 'flex', alignItems: 'center', justifyContent: 'center',
        }}>
          {Icon && <Icon size={15} color={a.icon} />}
        </div>
      </div>
    </div>
  );
}

function BodyLoading({ msg }) {
  return (
    <div style={{ padding: 60, textAlign: 'center', color: S[500] }}>
      <Loader size={26} style={{ animation: 'acpSpin 1s linear infinite' }} />
      <div style={{ fontSize: 13, marginTop: 12 }}>{msg || 'Loading…'}</div>
    </div>
  );
}

function BodyError({ msg, onRetry }) {
  return (
    <div style={{ padding: 20 }}>
      <div style={{
        padding: '14px 16px', background: ACCENT.rose.bg,
        border: `1px solid ${ACCENT.rose.border}`, color: ACCENT.rose.text,
        borderRadius: 10, display: 'flex', alignItems: 'center', gap: 10,
      }}>
        <AlertTriangle size={16} color={ACCENT.rose.icon} />
        <span style={{ flex: 1 }}>{msg}</span>
        {onRetry && (
          <button onClick={onRetry} style={{
            ...btnBase, padding: '5px 11px', fontSize: 11, fontWeight: 600,
            border: `1px solid ${ACCENT.rose.border}`, background: '#fff', color: ACCENT.rose.text,
          }}>Retry</button>
        )}
      </div>
    </div>
  );
}

function BodyEmpty({ msg }) {
  return (
    <div style={{
      padding: 60, textAlign: 'center', color: S[500],
      background: '#fff', border: `1px dashed ${S[200]}`, borderRadius: 12,
    }}>
      <CheckCircle size={32} color={S[300]} />
      <div style={{ fontSize: 13, marginTop: 12, color: S[700] }}>{msg}</div>
    </div>
  );
}

/* ─── Helpers + small style fragments ────────────────────────────────────── */

const codeStyle = {
  background: S[100], padding: '1px 6px', borderRadius: 4,
  fontFamily: "'IBM Plex Mono', monospace",
};

const filterLabel = {
  fontSize: 11, fontWeight: 600, color: S[700], cursor: 'pointer',
  display: 'inline-flex', alignItems: 'center',
};

const fLabelTxt = {
  fontSize: 10, fontWeight: 700, color: S[500],
  textTransform: 'uppercase', letterSpacing: '0.06em',
};

const selectStyle = {
  padding: '6px 10px', borderRadius: 6, border: `1px solid ${S[200]}`,
  fontSize: 11, color: S[700], cursor: 'pointer', background: '#fff',
  fontFamily: 'inherit',
};

const hdrCellAcp = {
  padding: '10px 14px', display: 'flex', alignItems: 'center',
};

const cellAcp = {
  padding: '10px 14px', overflow: 'hidden',
};

function fmtN(v) {
  if (v == null) return '—';
  if (typeof v === 'string') return v;
  if (v < 1000) return String(v);
  if (v < 1_000_000) return `${(v / 1000).toFixed(1)}k`;
  return `${(v / 1_000_000).toFixed(2)}M`;
}
function fmtMs(v) {
  if (v == null) return '—';
  if (v < 1000) return `${Math.round(v)} ms`;
  if (v < 60000) return `${(v / 1000).toFixed(2)} s`;
  return `${(v / 60000).toFixed(1)} m`;
}
function fmtAge(min) {
  if (min == null) return '—';
  if (min < 60) return `${min} min`;
  if (min < 1440) return `${(min / 60).toFixed(1)} hr`;
  return `${(min / 1440).toFixed(1)} d`;
}
function mb(v) { return v == null ? '—' : `${v} MB`; }
function s(v) { return v == null ? '—' : `${v}s`; }

function statusLabel(s) {
  if (!s) return '—';
  return s.replace(/^WORKFLOW_EXECUTION_STATUS_/, '');
}
function statusPill(s) {
  const norm = statusLabel(s).toLowerCase();
  const a = norm === 'running' ? ACCENT.sky
          : norm === 'completed' ? ACCENT.emerald
          : norm === 'failed' || norm === 'terminated' ? ACCENT.rose
          : ACCENT.slate;
  return {
    fontSize: 9, fontWeight: 700, padding: '2px 8px', borderRadius: 4,
    background: a.bg, border: `1px solid ${a.border}`, color: a.text,
    textTransform: 'uppercase', letterSpacing: '0.04em',
    fontFamily: "'IBM Plex Mono', monospace",
    display: 'inline-block',
  };
}
